import { REPLAY_SCHEMA_VERSION, type ReplayChunk, type ReplayEvent } from 'ProdantixSdk/web/replay/types';

/** The server's per-chunk event cap (`ReplayChunk` validation). The chunker flushes
 *  well below this; the clamp is a defensive ceiling. */
export const SERVER_MAX_EVENTS = 5000;

export interface ChunkerOptions {
  distinctId: () => string;
  masked: boolean;
  maxEvents: number;
  now: () => number;
  sessionId: string;
}

/** Batches replay events into `ReplayChunk`s. Flushes when the buffer reaches
 *  `maxEvents` (clamped below the server cap) or on an explicit `flush()` — never
 *  emits an empty chunk, which the server rejects. Each flush increments the session
 *  sequence so the processor can order and dedupe chunks. */
export class ReplayChunker {
  private buffer: ReplayEvent[] = [];
  private seq = 0;
  private readonly maxEvents: number;

  constructor(private readonly options: ChunkerOptions) {
    this.maxEvents = Math.min(Math.max(1, options.maxEvents), SERVER_MAX_EVENTS);
  }

  /** Buffers an event; returns a chunk to upload when the buffer reaches `maxEvents`. */
  add(event: ReplayEvent): ReplayChunk | null {
    this.buffer.push(event);
    if (this.buffer.length >= this.maxEvents) {
      return this.flush();
    }
    return null;
  }

  /** Drains the buffer into a chunk, or null when empty. */
  flush(): ReplayChunk | null {
    if (this.buffer.length === 0) {
      return null;
    }
    const events = this.buffer;
    this.buffer = [];
    const chunk: ReplayChunk = {
      distinct_id: this.options.distinctId(),
      events,
      masked: this.options.masked,
      schema_version: REPLAY_SCHEMA_VERSION,
      seq: this.seq,
      session_id: this.options.sessionId,
      timestamp: new Date(this.options.now()).toISOString(),
    };
    this.seq += 1;
    return chunk;
  }
}
